<template>
  <main class="compliance">
    <template v-if="isFrench">
      <h1>Confiance, Securite et Conformite</h1>
      <p><strong>Derniere mise a jour :</strong> 11 avril 2026</p>

      <p>
        Cette page centralise les informations juridiques et de securite de
        <strong>Wellnotes</strong> et complete nos
        <a href="/terms">Conditions d'utilisation</a>,
        <a href="/privacy">Politique de confidentialite</a> et
        <a href="/legal">Mentions legales</a>.
      </p>

      <h2>1. Conformite plateformes (Apple/Google)</h2>
      <p>
        Wellnotes est distribue via Apple App Store et Google Play. Les achats,
        abonnements, remboursements et restaurations d'achats sont egalement
        regis par les regles de la plateforme utilisee.
      </p>
      <p>
        Pour iOS, Apple n'est pas responsable du contenu du service ni du support,
        sauf obligations imperatives prevues par la loi.
      </p>

      <h2>2. Usage pedagogique et absence de conseil professionnel</h2>
      <p>
        Wellnotes est un outil pedagogique. Les contenus generes ne constituent pas
        un conseil juridique, medical, financier, psychologique ou professionnel.
        L'utilisateur reste responsable de toute verification avant utilisation.
      </p>

      <h2>3. Regles d'usage acceptable et securite</h2>
      <ul>
        <li>Interdiction des usages illicites, trompeurs, abusifs ou contrefaisants.</li>
        <li>Interdiction de malware, spam, scraping automatise ou tests intrusifs.</li>
        <li>Interdiction de contourner les limites d'acces, de facturation ou de securite.</li>
        <li>
          Interdiction d'importer des donnees sensibles de tiers sans base legale
          ou autorisation adequate.
        </li>
      </ul>

      <h2>4. Signalement de violation de droits</h2>
      <p>
        Si vous estimez qu'un contenu traite via Wellnotes porte atteinte a vos
        droits de propriete intellectuelle, adressez une notification a
        <a href="mailto:contact@wellnotes.ai">contact@wellnotes.ai</a>
        avec les elements permettant d'identifier les droits invoques et le
        contenu conteste.
      </p>
      <p>
        Une notification complete doit inclure vos coordonnees, la justification
        de titularite, la localisation precise du contenu et une declaration de
        bonne foi. Des informations complementaires peuvent etre demandees.
      </p>
      <p>
        Lorsqu'une procedure de contre-notification est prevue par la loi,
        l'utilisateur concerne peut soumettre une reponse argumentee.
      </p>

      <h2>5. Signalement abus, fraude et securite</h2>
      <p>
        Pour signaler un abus, une fraude, un compte compromis ou une faille de
        securite, contactez
        <a href="mailto:contact@wellnotes.ai?subject=Security%20or%20abuse%20report%20-%20Wellnotes">contact@wellnotes.ai</a>
        en joignant les preuves utiles (captures, horodatage, contexte).
      </p>

      <h2>6. Requisitions judiciaires et demandes reglementaires</h2>
      <p>
        Les demandes juridiquement valides peuvent etre adressees a
        <a href="mailto:contact@wellnotes.ai?subject=Legal%20Request%20-%20Wellnotes">contact@wellnotes.ai</a>
        et doivent preciser la base legale, l'autorite et le perimetre de la
        demande. Elles sont traitees conformement au droit applicable.
      </p>

      <h2>7. Droit applicable et droits des consommateurs</h2>
      <p>
        Wellnotes est exploite depuis la France. Les droits imperatifs des
        consommateurs prevus par la legislation locale applicable (notamment UE)
        restent pleinement applicables. Voir aussi les
        <a href="/terms">Conditions d'utilisation</a>.
      </p>

      <h2>8. Information mediation consommation UE</h2>
      <p>
        Avant toute procedure, une resolution amiable est privilegiee via le
        support. Les consommateurs de l'UE peuvent egalement recourir aux
        mecanismes extrajudiciaires disponibles selon leur droit national.
      </p>
      <p>
        En France, le recours a la mediation de la consommation est ouvert dans
        les conditions prevues par le Code de la consommation, notamment
        <strong>l'article L612-1</strong>.
      </p>

      <h2>9. Cadre normatif consommation UE</h2>
      <p>
        Les engagements d'information precontractuelle et de protection des
        consommateurs sont apprecies au regard de la
        <strong>directive 2011/83/UE</strong>, de la
        <strong>directive (UE) 2019/770</strong> et des dispositions nationales de
        transposition applicables.
      </p>

      <h2>10. Contact</h2>
      <p>
        Contact principal juridique et conformite :
        <a href="mailto:contact@wellnotes.ai">contact@wellnotes.ai</a>
      </p>
    </template>

    <template v-else>
      <h1>Trust, Safety and Compliance</h1>
      <p><strong>Last Updated:</strong> 11 April 2026</p>

      <p>
        This page centralizes legal, trust and safety information for
        <strong>Wellnotes</strong> and complements our
        <a href="/terms">Terms of Use</a>,
        <a href="/privacy">Privacy Policy</a>, and
        <a href="/legal">Legal Notice</a>.
      </p>

      <h2>1. Platform Compliance (Apple/Google)</h2>
      <p>
        Wellnotes is distributed through Apple App Store and Google Play.
        Purchases, subscriptions, and refunds are also subject to platform
        rules.
      </p>

      <h2>2. Educational Use Disclaimer</h2>
      <p>
        Wellnotes is an educational tool. Generated content is not legal,
        medical, financial, or professional advice.
      </p>

      <h2>3. Acceptable Use</h2>
      <ul>
        <li>No unlawful, abusive, or infringing use.</li>
        <li>No malware, spam, scraping abuse, or security probing.</li>
        <li>No bypass of access, billing, or safety controls.</li>
      </ul>

      <h2>4. Rights Infringement Reports</h2>
      <p>
        To report copyright or IP infringement, contact
        <a href="mailto:contact@wellnotes.ai">contact@wellnotes.ai</a>
        with sufficient details.
      </p>

      <h2>5. Abuse and Security Reports</h2>
      <p>
        To report abuse, fraud, account compromise, or vulnerability concerns,
        contact
        <a href="mailto:contact@wellnotes.ai?subject=Security%20or%20abuse%20report%20-%20Wellnotes">contact@wellnotes.ai</a>.
      </p>

      <h2>6. Legal and Regulatory Requests</h2>
      <p>
        Valid legal requests may be sent to
        <a href="mailto:contact@wellnotes.ai?subject=Legal%20Request%20-%20Wellnotes">contact@wellnotes.ai</a>
        and should include legal basis and scope.
      </p>

      <h2>7. Consumer Law (EU)</h2>
      <p>
        Consumer rights remain fully applicable under mandatory local law,
        including applicable EU rules.
      </p>

      <h2>8. Mediation and Out-of-Court Resolution</h2>
      <p>
        Before formal proceedings, users are encouraged to seek amicable
        resolution through support channels.
      </p>

      <h2>9. Contact</h2>
      <p>
        Main legal and compliance contact:
        <a href="mailto:contact@wellnotes.ai">contact@wellnotes.ai</a>
      </p>
    </template>
  </main>
  <AppFooter />
</template>

<script>
import AppFooter from "../components/AppFooter.vue";

export default {
  name: "CompliancePage",
  components: {
    AppFooter,
  },
  computed: {
    isFrench() {
      const rawLocale = this.$i18n?.locale;
      const locale =
        (typeof rawLocale === "string" ? rawLocale : rawLocale?.value) || "en";
      return locale.startsWith("fr");
    },
  },
};
</script>

<!-- Layout + typography styles live in src/styles/legal-pages.css (imported globally
     in main.js). -->
